import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { MemberIdentity } from '../members/MemberIdentity'
import { CompetitorProfileModal, type CompetitorTarget } from './CompetitorProfileModal'
import { MoveCompetitorModal } from './MoveCompetitorModal'
import {
  canMoveEntry,
  fullName,
  groupLabel,
  type CompetitionCategory,
  type CompetitionEntry,
  type CompetitionSession,
} from './types'

interface Props {
  entries: CompetitionEntry[]
  /** What a competitor can be moved between; without them the list is read-only. */
  categories?: CompetitionCategory[]
  sessions?: CompetitionSession[]
  /** The competition is over, so who is ranked where is the result. */
  locked?: boolean
  /** Only then is confirming meaningful, so the column is only then shown. */
  requiresConfirmation?: boolean
  onRefresh?: () => void
  /** Opens filtered to this category, as "Show competitors" on a category does. */
  initialCategory?: string | null
}

/** The filter key for "no filter"; never a real id, which are all uuids. */
const ALL = 'all'

/**
 * The competitor list, and on a competition that requires it, the registration
 * desk: staff tick a competitor off once they have arrived and paid, and only a
 * confirmed competitor may log anything.
 */
export function CompetitorsTab({
  entries,
  categories = [],
  sessions = [],
  locked,
  requiresConfirmation,
  onRefresh,
  initialCategory,
}: Props) {
  const { t } = useTranslation()
  const { primaryColor } = useGymContext()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [profileTarget, setProfileTarget] = useState<CompetitorTarget | null>(null)
  // Which row is being changed, so the spinner sits on the button pressed.
  const [busyId, setBusyId] = useState<string | null>(null)
  const [moveTarget, setMoveTarget] = useState<CompetitionEntry | null>(null)
  const [categoryFilter, setCategoryFilter] = useState(initialCategory ?? ALL)
  // The tab can stay mounted, so a later "Show competitors" must still apply.
  useEffect(() => {
    if (initialCategory) setCategoryFilter(initialCategory)
  }, [initialCategory])
  const [groupFilter, setGroupFilter] = useState(ALL)

  async function setConfirmed(entryId: string, confirmed: boolean) {
    setBusyId(entryId)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competition-entries/${entryId}/confirm`, {
        method: confirmed ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        addToast({ title: t('competitions.errorSaveFailed'), color: 'danger' })
        return
      }
      addToast({
        title: confirmed ? t('competitions.entryConfirmed') : t('competitions.entryUnconfirmed'),
        color: 'success',
      })
      onRefresh?.()
    } finally {
      setBusyId(null)
    }
  }

  const confirmedCount = entries.filter((e) => e.confirmed_at).length

  const inCategory = (id: string) => entries.filter((e) => e.category_id === id).length
  const inGroup = (id: string) => entries.filter((e) => e.session_id === id).length
  // A filter on a category or group that has since been deleted shows everyone
  // rather than an empty list with no way to tell why.
  const activeCategory = categories.some((c) => c.id === categoryFilter) ? categoryFilter : ALL
  const activeGroup = sessions.some((s) => s.id === groupFilter) ? groupFilter : ALL
  const visible = entries.filter(
    (e) =>
      (activeCategory === ALL || e.category_id === activeCategory) &&
      (activeGroup === ALL || e.session_id === activeGroup),
  )
  const allLabel = t('competitions.filterAll', { count: entries.length })

  if (entries.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardBody className="py-8 text-center text-foreground/60">
          {t('competitions.competitorsEmpty')}
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      {requiresConfirmation && (
        <p className="mb-2 text-sm text-foreground/60">
          {t('competitions.confirmedCount', {
            confirmed: confirmedCount,
            total: entries.length,
          })}
        </p>
      )}
      {(categories.length > 1 || sessions.length > 1) && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2">
          {categories.length > 1 && (
            <Select
              size="sm"
              label={t('competitions.colCategory')}
              selectedKeys={[activeCategory]}
              onSelectionChange={(keys) => {
                // Clicking the selected item again hands back an empty set.
                const val = Array.from(keys)[0]
                if (val) setCategoryFilter(String(val))
              }}
            >
              {[
                <SelectItem key={ALL} textValue={allLabel}>
                  {allLabel}
                </SelectItem>,
                ...categories.map((c) => (
                  <SelectItem key={c.id} textValue={`${c.name} (${inCategory(c.id)})`}>
                    {c.name} ({inCategory(c.id)})
                  </SelectItem>
                )),
              ]}
            </Select>
          )}
          {sessions.length > 1 && (
            <Select
              size="sm"
              label={t('competitions.colGroup')}
              selectedKeys={[activeGroup]}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0]
                if (val) setGroupFilter(String(val))
              }}
            >
              {[
                <SelectItem key={ALL} textValue={allLabel}>
                  {allLabel}
                </SelectItem>,
                ...sessions.map((s) => (
                  <SelectItem key={s.id} textValue={`${groupLabel(s)} (${inGroup(s.id)})`}>
                    {groupLabel(s)} ({inGroup(s.id)})
                  </SelectItem>
                )),
              ]}
            </Select>
          )}
        </div>
      )}
      {/* A five-column table does not fit a phone. It scrolls inside its own
          box, so the page itself never does. */}
      <div className="overflow-x-auto">
        <Table aria-label={t('competitions.tabCompetitors')} removeWrapper>
          <TableHeader>
            <TableColumn>{t('competitions.colMember')}</TableColumn>
            <TableColumn>{t('competitions.colCategory')}</TableColumn>
            <TableColumn>{t('competitions.colGroup')}</TableColumn>
            <TableColumn>
              {requiresConfirmation ? t('competitions.columnConfirmed') : ''}
            </TableColumn>
            <TableColumn> </TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('competitions.competitorsNoMatch')}>
            {visible.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  {/* Same identity and same profile modal as the Members page — a
                    competitor is just a member, and should look like one. */}
                  <button
                    type="button"
                    className="flex min-w-0 cursor-pointer items-center text-left"
                    onClick={() =>
                      setProfileTarget({ userId: entry.user_id, childId: entry.child_id ?? null })
                    }
                    title={t('members.viewProfile')}
                  >
                    <MemberIdentity
                      userId={entry.user_id}
                      name={fullName(entry.profile)}
                      nickname={entry.profile?.nickname}
                      email={entry.profile?.email}
                      avatarExt={entry.profile?.avatar_ext}
                      avatarUpdatedAt={entry.profile?.avatar_updated_at}
                      primaryColor={primaryColor}
                      size="sm"
                    />
                    {entry.child_id && (
                      <span className="ml-2 shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                        {t('members.childBadge')}
                      </span>
                    )}
                  </button>
                </TableCell>
                <TableCell>{entry.category?.name ?? '—'}</TableCell>
                <TableCell>
                  {entry.session ? groupLabel(entry.session) : t('competitions.groupAllDay')}
                </TableCell>
                <TableCell>
                  {requiresConfirmation ? (
                    <Button
                      size="sm"
                      variant={entry.confirmed_at ? 'flat' : 'solid'}
                      color={entry.confirmed_at ? 'success' : 'primary'}
                      isLoading={busyId === entry.id}
                      isDisabled={busyId !== null}
                      onPress={() => void setConfirmed(entry.id, !entry.confirmed_at)}
                    >
                      {entry.confirmed_at ? t('competitions.confirmed') : t('competitions.confirm')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell>
                  {!locked && canMoveEntry(entry, categories, sessions) ? (
                    <Button size="sm" variant="light" onPress={() => setMoveTarget(entry)}>
                      {t('competitions.moveCompetitor')}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MoveCompetitorModal
        entry={moveTarget}
        categories={categories}
        sessions={sessions}
        onClose={() => setMoveTarget(null)}
        onMoved={() => {
          addToast({ title: t('competitions.competitorMoved'), color: 'success' })
          onRefresh?.()
        }}
      />

      {profileTarget && (
        <CompetitorProfileModal target={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </>
  )
}
